/**
 * Von Grundle Dialogue Engine (data-driven)
 *
 * Reads the full dialogue tree from window.VG_DIALOGUE_TREE (assets/dialogue-tree.js,
 * generated from quiz/von-grundle-dialogue-tree.json) and drives the multi-branch
 * consultation: greetings, stage prompts/options, interventions, and exits.
 *
 * DOM contract (sections/von-grundle-dialogue.liquid):
 *   #vg-dialogue-text   - Von Grundle's current line (aria-live="polite", typewriter target)
 *   #vg-options         - empty container the engine fills with <button class="vg-option">
 *   #vg-response-area   - hidden block; holds #vg-response-text plus appended .vg-intervention divs
 *   #vg-response-text   - Von Grundle's reaction to the chosen option (aria-live="polite")
 *   #vg-continue-btn    - single action button; label/behavior set by the engine
 *
 * Data expected from the section:
 *   window.VGShopifyData = { customer: { firstName } | null, quizCompleted: bool }
 */

const VGDialogue = (function() {
  'use strict';

  // ---------------------------------------------------------------------------
  // Tree + configuration
  // ---------------------------------------------------------------------------

  // Minimal fallback so the section never dead-ends if dialogue-tree.js fails to load.
  const FALLBACK_TREE = {
    greetings: {
      loggedIn: {
        firstVisit: { text: "Ah... *[firstName]*. I've been expecting you. The tea leaves were... specific this morning." },
        returnVisit: { text: "Ah... *[firstName]*. Back again? *[checks imaginary ledger]* You DO seem to accumulate grievances." }
      },
      guest: { text: "Another seeker of arcane redress, I see. The rain always brings them. Come, come—don't drip on the grimoires." }
    },
    stages: {
      stage1: {
        id: 'purpose',
        prompt: 'So then... what brings you to seek the counsel of Von Grundle this *[timeOfDay]*?',
        options: [
          { id: 'vengeance', label: 'Someone has wronged me.', response: 'Ah, yes. The classic. *[settles into chair, steeples fingers]* Tell me of this... transgression.', goTo: 'vgvpi_quiz', tags: ['vengeance'] },
          { id: 'gift', label: 'I seek a gift for someone deserving.', response: 'Generosity? In THIS economy? *[genuinely surprised]* How... refreshing.', goTo: 'vgvpi_quiz', tags: ['gift'] },
          { id: 'browse', label: "I'm just looking around.", response: '*[long, evaluating stare]*\n\nBrowsing. In MY emporium.\n\n...Very well. But touch nothing that hums.', goTo: 'exit_to_shop', tags: ['browse'] },
          { id: 'uncertain', label: "I'm not entirely sure.", response: '*[understanding nod]*\n\nThen we shall divine your purpose together.', goTo: 'vgvpi_quiz', tags: ['uncertain'] }
        ]
      }
    },
    exits: {
      vgvpi_quiz: { type: 'quiz', quizType: 'full', intro: 'Now then. Let us assess your temperament in more detail...' },
      exit_to_shop: { type: 'shop' }
    },
    interventions: {},
    productMappings: {},
    dynamicText: { timeOfDay: { morning: 'morning', afternoon: 'afternoon', evening: 'evening', night: 'witching hour' } }
  };

  const TREE = (window.VG_DIALOGUE_TREE && window.VG_DIALOGUE_TREE.stages) ? window.VG_DIALOGUE_TREE : FALLBACK_TREE;

  const CONFIG = {
    typewriterSpeed: (TREE.config && TREE.config.typewriterSpeed) || 30, // ms per character
    sentencePause: 280,     // extra ms after . ? !
    commaPause: 120,        // extra ms after , ; :
    stageDirectionPause: 350, // extra ms entering/leaving a *[stage direction]*
    betweenLinesDelay: 600, // pause between greeting and first prompt
    afterTextDelay: 200     // pause after a line completes before options/buttons appear
  };

  // Collection handles known to exist in the store. Add handles here (e.g.
  // 'cozy-evening-collection', 'midnight-storm-collection') once the collections
  // are created in the Shopify admin; until then, mapped exits fall back to /collections/all.
  const KNOWN_COLLECTIONS = [];

  const DEFAULT_EXIT_INTROS = {
    shop: 'Go then. Browse freely. *[gestures toward the shelves]* Touch nothing that hums.',
    quiz: 'Now then. Let us continue your consultation...',
    collection: 'I believe I know just the collection to suit your... particular tastes.',
    previous_results: 'Ah yes. Let us revisit what the consultation revealed...'
  };

  const EXIT_BUTTON_LABELS = {
    quiz: 'Begin the Assessment',
    collection: 'Behold the Collection',
    shop: 'Enter the Emporium',
    previous_results: 'Review the Findings'
  };

  // Icons for the stage-1 purpose options (preserves the original look).
  const OPTION_ICONS = {
    vengeance: '⚔️',
    gift: '🎁',
    browse: '👁️',
    uncertain: '❓'
  };

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  let state = {
    currentStage: 'greeting',
    collectedTags: [],
    dragonHintCount: 0,
    path: null,
    shownInterventions: []
  };

  let elements = {};
  let pendingAction = null; // continue-button behavior
  let activeRun = null;     // current typewriter run

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    elements = {
      card: document.querySelector('.vg-dialogue-card'),
      dialogueText: document.getElementById('vg-dialogue-text'),
      options: document.getElementById('vg-options'),
      responseArea: document.getElementById('vg-response-area'),
      responseText: document.getElementById('vg-response-text'),
      continueBtn: document.getElementById('vg-continue-btn')
    };

    if (!elements.dialogueText || !elements.options) return;

    // Click anywhere on the card (not a button) skips the typewriter.
    if (elements.card) {
      elements.card.addEventListener('click', function(e) {
        if (e.target.closest('button')) return;
        skipTypewriter();
      });
    }

    if (elements.continueBtn) {
      elements.continueBtn.addEventListener('click', function() {
        const action = pendingAction;
        pendingAction = null;
        elements.continueBtn.classList.remove('visible');
        if (action) action();
      });
    }

    loadState();

    // Resume a saved mid-conversation stage; otherwise start from the greeting.
    if (state.currentStage && TREE.stages && TREE.stages[state.currentStage]) {
      renderStage(state.currentStage);
    } else {
      showGreeting();
    }
  }

  // ---------------------------------------------------------------------------
  // Text processing
  // ---------------------------------------------------------------------------

  function getTimeOfDay() {
    const hour = new Date().getHours();
    let key;
    if (hour >= 5 && hour < 12) key = 'morning';
    else if (hour < 17) key = 'afternoon';
    else if (hour < 21) key = 'evening';
    else key = 'night';
    const map = (TREE.dynamicText && TREE.dynamicText.timeOfDay) || {};
    return map[key] || (key === 'night' ? 'witching hour' : key);
  }

  function processText(text) {
    const shopifyData = window.VGShopifyData || {};
    const firstName = (shopifyData.customer && shopifyData.customer.firstName) || 'traveler';
    return String(text)
      .replace(/\*\[firstName\]\*/g, firstName)
      .replace(/\*\[timeOfDay\]\*/g, getTimeOfDay());
  }

  /**
   * Tokenize dialogue text into render segments:
   *   { br: true }                                  -> <br>
   *   { cls: 'vg-stage-direction', text: '*[..]*' } -> styled span
   *   { cls: 'vg-emphasis', text: 'CAPS' }          -> styled span
   *   { cls: null, text: '...' }                    -> plain text
   */
  function segmentize(text) {
    const segments = [];
    const stageRe = /\*\[([^\]]+)\]\*/g;
    let last = 0;
    let match;
    while ((match = stageRe.exec(text)) !== null) {
      if (match.index > last) segmentizePlain(text.slice(last, match.index), segments);
      segments.push({ cls: 'vg-stage-direction', text: match[0] });
      last = match.index + match[0].length;
    }
    if (last < text.length) segmentizePlain(text.slice(last), segments);
    return segments;
  }

  function segmentizePlain(chunk, segments) {
    const lines = chunk.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) segments.push({ br: true });
      const line = lines[i];
      const capsRe = /\b([A-Z]{2,})\b/g;
      let last = 0;
      let match;
      while ((match = capsRe.exec(line)) !== null) {
        if (match.index > last) segments.push({ cls: null, text: line.slice(last, match.index) });
        segments.push({ cls: 'vg-emphasis', text: match[1] });
        last = match.index + match[1].length;
      }
      if (last < line.length) segments.push({ cls: null, text: line.slice(last) });
    }
  }

  function buildFragment(segments) {
    const frag = document.createDocumentFragment();
    segments.forEach(function(seg) {
      if (seg.br) {
        frag.appendChild(document.createElement('br'));
        return;
      }
      if (seg.cls) {
        const span = document.createElement('span');
        span.className = seg.cls;
        span.textContent = seg.text;
        frag.appendChild(span);
      } else {
        frag.appendChild(document.createTextNode(seg.text));
      }
    });
    return frag;
  }

  // ---------------------------------------------------------------------------
  // Typewriter
  // ---------------------------------------------------------------------------

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Type Von Grundle's line into `el` (~30ms/char, longer pauses on . ? ! and at
   * *[stage direction]* boundaries). While animating, characters live inside an
   * aria-hidden wrapper; on completion the element (aria-live="polite") receives
   * the final content once, so screen readers hear the whole line exactly once.
   * Click-to-skip and prefers-reduced-motion both render instantly.
   */
  function speak(el, rawText, onComplete) {
    if (activeRun) activeRun.cancelled = true;

    const segments = segmentize(processText(rawText));
    const run = { cancelled: false, finished: false, timer: null };
    activeRun = run;

    function finish() {
      if (run.finished || run.cancelled) return;
      run.finished = true;
      if (run.timer) clearTimeout(run.timer);
      el.innerHTML = '';
      el.appendChild(buildFragment(segments));
      if (activeRun === run) activeRun = null;
      if (onComplete) setTimeout(onComplete, CONFIG.afterTextDelay);
    }
    run.finish = finish;

    if (prefersReducedMotion() || segments.length === 0) {
      finish();
      return;
    }

    el.innerHTML = '';
    const wrap = document.createElement('span');
    wrap.setAttribute('aria-hidden', 'true');
    el.appendChild(wrap);

    let segIndex = 0;
    let charIndex = 0;
    let currentSpan = null;

    function delayAfterChar(ch) {
      if (ch === '.' || ch === '?' || ch === '!') return CONFIG.typewriterSpeed + CONFIG.sentencePause;
      if (ch === ',' || ch === ';' || ch === ':') return CONFIG.typewriterSpeed + CONFIG.commaPause;
      return CONFIG.typewriterSpeed;
    }

    function step() {
      if (run.cancelled || run.finished) return;
      if (segIndex >= segments.length) {
        finish();
        return;
      }

      const seg = segments[segIndex];

      if (seg.br) {
        wrap.appendChild(document.createElement('br'));
        segIndex++;
        charIndex = 0;
        currentSpan = null;
        run.timer = setTimeout(step, CONFIG.typewriterSpeed);
        return;
      }

      if (charIndex === 0) {
        currentSpan = document.createElement('span');
        if (seg.cls) currentSpan.className = seg.cls;
        wrap.appendChild(currentSpan);
        if (seg.cls === 'vg-stage-direction') {
          // dramatic beat before the stage direction begins
          run.timer = setTimeout(function() { typeNext(seg); }, CONFIG.stageDirectionPause);
          return;
        }
      }
      typeNext(seg);
    }

    function typeNext(seg) {
      if (run.cancelled || run.finished) return;
      const ch = seg.text.charAt(charIndex);
      currentSpan.textContent += ch;
      charIndex++;
      if (charIndex >= seg.text.length) {
        const trailing = seg.cls === 'vg-stage-direction' ? CONFIG.stageDirectionPause : delayAfterChar(ch);
        segIndex++;
        charIndex = 0;
        currentSpan = null;
        run.timer = setTimeout(step, trailing);
      } else {
        run.timer = setTimeout(function() { typeNext(seg); }, delayAfterChar(ch));
      }
    }

    step();
  }

  function skipTypewriter() {
    if (activeRun && !activeRun.finished && activeRun.finish) {
      activeRun.finish();
    }
  }

  // ---------------------------------------------------------------------------
  // Greeting
  // ---------------------------------------------------------------------------

  function showGreeting() {
    const greetings = TREE.greetings || {};
    const shopifyData = window.VGShopifyData || {};
    let node = null;
    let overrideOptions = null;

    if (shopifyData.customer) {
      const returning = localStorage.getItem('vg_quiz_completed') === 'true' || shopifyData.quizCompleted === true;
      const loggedIn = greetings.loggedIn || {};
      if (returning && loggedIn.returnVisit) {
        node = loggedIn.returnVisit;
        overrideOptions = loggedIn.returnVisit.overrideOptions || null;
      } else {
        node = loggedIn.firstVisit || greetings.guest;
      }
    } else {
      node = greetings.guest;
    }

    const text = (node && node.text) || 'Welcome to my emporium, seeker.';
    state.currentStage = 'greeting';
    saveState();

    speak(elements.dialogueText, text, function() {
      if (overrideOptions && overrideOptions.length) {
        renderOptions(overrideOptions);
      } else {
        setTimeout(function() { renderStage('stage1'); }, CONFIG.betweenLinesDelay);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Stages & options
  // ---------------------------------------------------------------------------

  function renderStage(stageId) {
    const stage = TREE.stages && TREE.stages[stageId];
    if (!stage) {
      window.location.href = '/collections/all';
      return;
    }

    // stage1 is only ever entered at the START of a consultation (first visit or
    // the return-visitor "new problem" path). Reset the consultation-scoped state
    // so a fresh consultation never inherits tags, dragon-hints, or already-shown
    // interventions from a prior (persisted) session.
    if (stageId === 'stage1') {
      state.collectedTags = [];
      state.dragonHintCount = 0;
      state.path = null;
      state.shownInterventions = [];
    }

    state.currentStage = stageId;
    saveState();

    clearResponse();
    clearOptions();

    speak(elements.dialogueText, stage.prompt, function() {
      renderOptions(stage.options || []);
    });
  }

  function renderOptions(options) {
    elements.options.innerHTML = '';
    options.forEach(function(option) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vg-option';
      btn.dataset.option = option.id;

      if (OPTION_ICONS[option.id]) {
        const icon = document.createElement('span');
        icon.className = 'vg-option-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = OPTION_ICONS[option.id];
        btn.appendChild(icon);
      }

      const label = document.createElement('span');
      label.textContent = option.label;
      btn.appendChild(label);

      btn.addEventListener('click', function() { handleSelect(option, btn); });
      elements.options.appendChild(btn);
    });
    elements.options.classList.add('visible');
  }

  function clearOptions() {
    elements.options.classList.remove('visible');
    elements.options.innerHTML = '';
  }

  function handleSelect(option, btnEl) {
    // Lock the choice in visually
    Array.prototype.forEach.call(elements.options.children, function(btn) {
      btn.disabled = true;
      if (btn === btnEl) btn.classList.add('selected');
      else btn.classList.add('dimmed');
    });

    // Record the choice
    if (state.currentStage === 'stage1') state.path = option.id;
    (option.tags || []).forEach(function(tag) {
      if (state.collectedTags.indexOf(tag) === -1) state.collectedTags.push(tag);
    });
    if (option.dragonHint === true) state.dragonHintCount++;
    saveState();

    const interventions = collectTriggeredInterventions();
    saveState(); // persist shownInterventions

    // Return-visit override options carry no response: advance immediately.
    if (!option.response) {
      performGoTo(option.goTo);
      return;
    }

    elements.responseArea.classList.add('visible');
    speak(elements.responseText, option.response, function() {
      playInterventions(interventions, 0, function() {
        setContinue('Continue...', function() { performGoTo(option.goTo); });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Interventions
  // ---------------------------------------------------------------------------

  function collectTriggeredInterventions() {
    const triggered = [];
    const interventions = TREE.interventions || {};
    Object.keys(interventions).forEach(function(key) {
      if (state.shownInterventions.indexOf(key) !== -1) return;
      const trigger = interventions[key].trigger || {};
      let fires = false;
      if (trigger.requires && trigger.requires.length) {
        fires = trigger.requires.every(function(tag) {
          return state.collectedTags.indexOf(tag) !== -1;
        });
      }
      if (!fires && typeof trigger.dragonHintCount === 'number') {
        fires = state.dragonHintCount >= trigger.dragonHintCount;
      }
      if (fires) {
        state.shownInterventions.push(key);
        triggered.push(interventions[key]);
      }
    });
    return triggered;
  }

  function playInterventions(list, index, done) {
    if (index >= list.length) {
      done();
      return;
    }
    const block = document.createElement('div');
    block.className = 'vg-intervention';
    block.setAttribute('aria-live', 'polite');
    elements.responseArea.appendChild(block);
    speak(block, list[index].text, function() {
      playInterventions(list, index + 1, done);
    });
  }

  // ---------------------------------------------------------------------------
  // Routing: stages and exits
  // ---------------------------------------------------------------------------

  function performGoTo(goTo) {
    if (goTo && TREE.stages && TREE.stages[goTo]) {
      renderStage(goTo);
      return;
    }
    if (goTo && TREE.exits && TREE.exits[goTo]) {
      handleExit(goTo);
      return;
    }
    // Unknown destination: never dead-end.
    window.location.href = '/collections/all';
  }

  function handleExit(exitId) {
    const exit = (TREE.exits && TREE.exits[exitId]) || { type: 'shop' };

    // Reaching any exit counts as a completed consultation.
    try { localStorage.setItem('vg_quiz_completed', 'true'); } catch (e) { /* storage unavailable */ }
    state.currentStage = 'exit:' + exitId;
    saveState();

    clearResponse();
    clearOptions();

    const intro = exit.intro || DEFAULT_EXIT_INTROS[exit.type] || DEFAULT_EXIT_INTROS.shop;
    speak(elements.dialogueText, intro, function() {
      const label = EXIT_BUTTON_LABELS[exit.type] || 'Onward';
      setContinue(label, function() { performExitNavigation(exit); });
    });
  }

  function performExitNavigation(exit) {
    switch (exit.type) {
      case 'collection':
        window.location.href = resolveCollectionUrl();
        break;
      case 'shop':
        window.location.href = '/collections/all';
        break;
      case 'quiz':
      case 'previous_results':
      default:
        goToQuizOrShop();
    }
  }

  /**
   * Map collected browse tags through tree.productMappings to a collection URL.
   * Only navigates to a mapped collection when its handle is in KNOWN_COLLECTIONS
   * (i.e. it exists in the store); otherwise degrades to /collections/all.
   */
  function resolveCollectionUrl() {
    const mappings = TREE.productMappings || {};
    const tags = state.collectedTags;
    function has(tag) { return tags.indexOf(tag) !== -1; }

    let handle = null;
    if ((has('vibe_cozy') || has('evening_cozy')) && has('scent_warm')) {
      handle = mappings.browse_cozy_warm && mappings.browse_cozy_warm.collection;
    } else if ((has('vibe_dramatic') || has('evening_dramatic')) && has('scent_cool')) {
      handle = mappings.browse_dramatic_cool && mappings.browse_dramatic_cool.collection;
    }

    if (handle && KNOWN_COLLECTIONS.indexOf(handle) !== -1) {
      return '/collections/' + handle;
    }
    return '/collections/all';
  }

  /**
   * The dedicated quiz pages do not exist; if a quiz section shares this page
   * (as on page.quiz), scroll to it. Otherwise degrade gracefully to the shop.
   */
  function goToQuizOrShop() {
    const quizSection = document.querySelector('.quiz-section, section[id*="quiz"]');
    if (quizSection) {
      quizSection.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      quizSection.style.transition = 'box-shadow 0.3s ease';
      quizSection.style.boxShadow = '0 0 20px rgba(184, 134, 11, 0.4)';
      setTimeout(function() { quizSection.style.boxShadow = ''; }, 1500);
    } else {
      window.location.href = '/collections/all';
    }
  }

  // ---------------------------------------------------------------------------
  // Continue button / response area helpers
  // ---------------------------------------------------------------------------

  function setContinue(label, action) {
    if (!elements.continueBtn) {
      // No button available: fail open rather than dead-end.
      if (action) action();
      return;
    }
    pendingAction = action;
    elements.continueBtn.textContent = label;
    elements.continueBtn.classList.add('visible');
  }

  function clearResponse() {
    pendingAction = null;
    if (elements.continueBtn) elements.continueBtn.classList.remove('visible');
    if (elements.responseArea) {
      elements.responseArea.classList.remove('visible');
      Array.prototype.slice.call(elements.responseArea.querySelectorAll('.vg-intervention')).forEach(function(node) {
        node.parentNode.removeChild(node);
      });
    }
    if (elements.responseText) elements.responseText.innerHTML = '';
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  function saveState() {
    try {
      localStorage.setItem('vg_dialogue_state', JSON.stringify({
        currentStage: state.currentStage,
        collectedTags: state.collectedTags,
        dragonHintCount: state.dragonHintCount,
        path: state.path,
        shownInterventions: state.shownInterventions,
        timestamp: Date.now()
      }));
    } catch (e) { /* storage unavailable */ }
  }

  function loadState() {
    try {
      const saved = localStorage.getItem('vg_dialogue_state');
      if (!saved) return;
      const data = JSON.parse(saved);
      // Only restore if less than 1 hour old
      if (Date.now() - data.timestamp < 3600000) {
        state.currentStage = data.currentStage || 'greeting';
        state.collectedTags = data.collectedTags || [];
        state.dragonHintCount = data.dragonHintCount || 0;
        state.path = data.path || null;
        state.shownInterventions = data.shownInterventions || [];
      }
    } catch (e) {
      console.warn('Could not load VG dialogue state:', e);
    }
  }

  /**
   * Reset dialogue (for testing or restart)
   */
  function reset() {
    localStorage.removeItem('vg_dialogue_state');
    localStorage.removeItem('vg_quiz_completed');
    location.reload();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  const api = {
    reset: reset,
    skip: skipTypewriter,
    getState: function() { return JSON.parse(JSON.stringify(state)); },
    // Compatibility shims for any legacy inline handlers
    selectOption: function(optionId) {
      const btn = elements.options && elements.options.querySelector('.vg-option[data-option="' + optionId + '"]');
      if (btn && !btn.disabled) btn.click();
    },
    continueDialogue: function() {
      if (elements.continueBtn && elements.continueBtn.classList.contains('visible')) elements.continueBtn.click();
    }
  };
  window.VGDialogue = api;
  return api;
})();
